// src/components/upmode-agent/email-confirm-card.tsx
import React, { useState } from "react";
import { Mail, Send, X, Edit3, AlertTriangle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";

export interface PendingEmail {
  tool_use_id: string;
  to_email: string;
  subject: string;
  body: string;
}

interface EmailConfirmCardProps {
  email: PendingEmail;
  onConfirm: (edited?: Partial<PendingEmail>) => void;
  onCancel: () => void;
}

function isValidEmail(addr: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr.trim());
}

export function EmailConfirmCard({ email, onConfirm, onCancel }: EmailConfirmCardProps) {
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState(email.to_email);
  const [subject, setSubject] = useState(email.subject);
  const [body, setBody] = useState(email.body);

  const emailValid = isValidEmail(to);

  const handleSend = () => {
    if (!emailValid) return;
    onConfirm({ to_email: to.trim(), subject: subject.trim(), body: body.trim() });
  };

  return (
    <div
      style={{
        alignSelf: "flex-start",
        maxWidth: "95%",
        width: "100%",
        borderRadius: 12,
        border: "2px solid rgba(239,68,68,0.25)",
        backgroundColor: "rgba(239,68,68,0.04)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 0,
      }}
    >
      {/* Header — shared Alert component with edit toggle action */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Alert variant="destructive">
            <Mail />
            <AlertTitle>Confirm Email Send</AlertTitle>
            <AlertDescription>
              The agent wants to send an email — review and approve before it goes out.
            </AlertDescription>
          </Alert>
        </div>
        <button
          onClick={() => setEditing(!editing)}
          style={{
            padding: 7,
            borderRadius: 7,
            border: "1px solid var(--border)",
            backgroundColor: editing ? "rgba(37,99,235,0.08)" : "var(--background)",
            cursor: "pointer",
            color: editing ? "#2563EB" : "var(--muted-foreground)",
            flexShrink: 0,
            height: "fit-content",
          }}
          title={editing ? "Stop editing" : "Edit before sending"}
          aria-label={editing ? "Stop editing" : "Edit before sending"}
        >
          <Edit3 style={{ width: 12, height: 12 }} />
        </button>
      </div>

      {/* Fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, backgroundColor: "var(--background)", borderRadius: 8, padding: 10, border: "1px solid var(--border)", minWidth: 0 }}>
        {/* To */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>To</div>
          {editing ? (
            <>
              <input
                value={to}
                onChange={e => setTo(e.target.value)}
                type="email"
                placeholder="recipient@example.com"
                style={{ width: "100%", padding: "6px 9px", borderRadius: 6, border: `1px solid ${emailValid ? "var(--border)" : "#EF4444"}`, fontSize: 12, outline: "none", boxSizing: "border-box", backgroundColor: "var(--muted)", color: "var(--foreground)" }}
              />
              {!emailValid && (
                <div className="mt-1">
                  <Alert variant="destructive" className="py-2">
                    <AlertTriangle />
                    <AlertDescription>Please enter a valid email address.</AlertDescription>
                  </Alert>
                </div>
              )}
            </>
          ) : (
            <div style={{
              fontSize: 12,
              color: "var(--foreground)",
              fontWeight: 600,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}>
              {to || <em style={{ color: "#EF4444" }}>missing</em>}
            </div>
          )}
        </div>

        {/* Subject */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Subject</div>
          {editing ? (
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={{ width: "100%", padding: "6px 9px", borderRadius: 6, border: "1px solid var(--border)", fontSize: 12, outline: "none", boxSizing: "border-box", backgroundColor: "var(--muted)", color: "var(--foreground)" }}
            />
          ) : (
            <div style={{
              fontSize: 12,
              color: "var(--foreground)",
              fontWeight: 500,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}>
              {subject}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Message</div>
          {editing ? (
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={8}
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid var(--border)", fontSize: 12, outline: "none", boxSizing: "border-box", backgroundColor: "var(--muted)", fontFamily: "inherit", lineHeight: 1.55, resize: "vertical", color: "var(--foreground)" }}
            />
          ) : (
            <div style={{
              fontSize: 12,
              color: "var(--foreground)",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              maxHeight: 220,
              overflowY: "auto",
              padding: "4px 0",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}>
              {body}
            </div>
          )}
        </div>
      </div>

      {/* Actions — wrap to next line if narrow */}
      <div style={{
        display: "flex",
        gap: 8,
        justifyContent: "flex-end",
        flexWrap: "wrap",
      }}>
        <button
          onClick={onCancel}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "7px 13px", borderRadius: 7, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--foreground)", flex: "0 1 auto" }}
        >
          <X style={{ width: 11, height: 11 }} />
          Cancel
        </button>
        <button
          onClick={handleSend}
          disabled={!emailValid}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: "none", backgroundColor: emailValid ? "#10B981" : "var(--border)", color: "white", cursor: emailValid ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 600, flex: "0 1 auto" }}
        >
          <Send style={{ width: 11, height: 11 }} />
          Send Email
        </button>
      </div>
    </div>
  );
}